// src/services/PreferenceService.ts
/**
 * Preference API Service
 * Handles all preference-related API calls and SSE streams
 */

import axios from 'axios';
import { supabase } from '../lib/supabaseClient.ts';
import {
  UserPreferences,
  PreferenceUpdateRequest,
  PreferenceSectionUpdateRequest,
  PreferenceMergeResponse,
} from '../types/preferences.ts';

// API base URL
const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.autotuneresume.com/api/v1';

console.log(`Preference API URL: ${BASE_URL}`);

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
  timeout: 30000,
});

// Helper to attach JWT token
async function attachToken(config: any) {
  const { data } = await supabase.auth.getSession();
  const sbToken = data.session?.access_token;
  const headers = (config.headers = config.headers || {});
  if (sbToken) {
    headers.Authorization = `Bearer ${sbToken}`;
    return config;
  }
  // Fallback to backend token
  try {
    const ls = typeof window !== 'undefined' ? window.localStorage : undefined;
    const backendToken = ls?.getItem('authToken');
    const backendTokenType = ls?.getItem('authTokenType') || 'Bearer';
    if (backendToken) {
      headers.Authorization = `${backendTokenType} ${backendToken}`;
    }
  } catch (e) {
    // ignore
  }
  return config;
}

api.interceptors.request.use(attachToken);

// ============================================================================
// API Methods
// ============================================================================

export class PreferenceService {
  /**
   * Get user preferences
   */
  static async getPreferences(includeOnboardingInfo: boolean = true): Promise<UserPreferences> {
    const response = await api.get('/me/preferences', {
      params: { include_onboarding_info: includeOnboardingInfo },
    });
    return response.data;
  }

  /**
   * Create or update full preference profile
   */
  static async updatePreferences(
    preferences: PreferenceUpdateRequest
  ): Promise<PreferenceMergeResponse> {
    const response = await api.post('/me/preferences', preferences);
    return response.data;
  }

  /**
   * Update a specific preference section
   */
  static async updateSection(
    sectionName: string,
    data: any,
    merge: boolean = true
  ): Promise<PreferenceMergeResponse> {
    const request: PreferenceSectionUpdateRequest = { data, merge };
    const response = await api.patch(`/me/preferences/sections/${sectionName}`, request);
    return response.data;
  }

  /**
   * Mark a section as user-confirmed
   */
  static async confirmSection(sectionName: string): Promise<PreferenceMergeResponse> {
    const response = await api.post(`/me/preferences/sections/${sectionName}/confirm`, {
      confirmed: true,
    });
    return response.data;
  }

  /**
   * Delete user preferences
   */
  /**
   * Delete user preferences
   */
  static async deletePreferences(): Promise<{ success: boolean; message: string }> {
    const response = await api.delete('/me/preferences');
    return response.data;
  }

  /**
   * Create EventSource for preference SSE stream
   * Returns an EventSource instance that the caller should manage
   */
  static async createPreferenceStream(): Promise<EventSource> {
    // Get auth token
    const { data } = await supabase.auth.getSession();
    const sbToken = data.session?.access_token;

    if (!sbToken) {
      // Fallback to backend token
      const backendToken = localStorage.getItem('authToken');
      if (!backendToken) {
        throw new Error('No authentication token available');
      }
    }

    const token = sbToken || localStorage.getItem('authToken') || '';

    // Create EventSource with token as query param (EventSource doesn't support headers)
    const url = `${BASE_URL}/stream/preferences?token=${encodeURIComponent(token)}`;
    const eventSource = new EventSource(url);

    return eventSource;
  }
}

export default PreferenceService;
